import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faLayerGroup, faTimes, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

function formatDeadline(date) {
  if (!date) return null;
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const TerrainTransitionModal = ({ isOpen, onClose, onConfirm, project, loading, addOnly }) => {
  const [waves, setWaves] = useState([]);
  const [standaloneSlots, setStandaloneSlots] = useState([{ name: '' }]);
  const [notes, setNotes] = useState('');
  // Prefill with existing data when modal opens — non-blocking
  useEffect(() => {
    if (!isOpen || !project?.id) return;

    // Reset to defaults immediately (no spinner)
    setWaves([]);
    setStandaloneSlots([{ name: '' }]);
    setNotes('');

    // Then try to load existing slots in background
    Promise.all([
      api.get(`/projects/${project.id}/slots`),
      api.get(`/projects/${project.id}/budgets`).catch(() => ({ data: { data: { budgets: [] } } })),
    ]).then(([slotsRes, budgetsRes]) => {
      const data = slotsRes.data.data || {};
      const existingWaves = data.waves || [];
      const existingStandalone = data.standalone_slots || [];
      const budgets = budgetsRes.data.data?.budgets || [];

      const isLocked = (slot) => {
        const isLinked = !!slot.survey_id;
        const budget = budgets.find(b => b.survey_slot_id === slot.id);
        const hasBudget = budget && ['submitted', 'approved'].includes(budget.status);
        const inPipeline = slot.step && slot.step !== 'setup';
        return isLinked || hasBudget || inPipeline;
      };

      if (existingWaves.length > 0 || existingStandalone.length > 0) {
        setWaves(existingWaves.map(w => ({
          id: w.id,
          name: w.name,
          locked: (w.survey_slots || []).some(s => isLocked(s)),
          slots: (w.survey_slots || []).map(s => ({ name: s.name, locked: isLocked(s) })),
        })).filter(w => w.slots.length > 0));

        const standalone = existingStandalone.map(s => ({ name: s.name, locked: isLocked(s) }));
        if (standalone.length > 0) setStandaloneSlots(standalone);
      }
    }).catch(() => {});
  }, [isOpen, project?.id]);

  const handleClose = () => { setWaves([]); setStandaloneSlots([{ name: '' }]); setNotes(''); onClose(); };
  // Editable form (groups, slots, notes): only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(handleClose, { backdropClose: false, enabled: isOpen && !!project });

  if (!isOpen || !project) return null;

  const setupDeadline = project.setup_deadline;

  const addWave = () => setWaves(prev => [...prev, { id: null, name: '', slots: [{ name: '' }] }]);
  const removeWave = (wi) => setWaves(prev => prev.filter((_, i) => i !== wi));
  const updateWaveName = (wi, name) => setWaves(prev => prev.map((w, i) => i === wi ? { ...w, name } : w));
  const addWaveSlot = (wi) => setWaves(prev => prev.map((w, i) => i === wi ? { ...w, slots: [...w.slots, { name: '' }] } : w));
  const removeWaveSlot = (wi, si) => setWaves(prev => prev.map((w, i) => i === wi ? { ...w, slots: w.slots.filter((_, j) => j !== si) } : w));
  const updateWaveSlotName = (wi, si, name) => setWaves(prev => prev.map((w, i) => i === wi ? { ...w, slots: w.slots.map((s, j) => j === si ? { ...s, name } : s) } : w));
  const addStandalone = () => setStandaloneSlots(prev => [...prev, { name: '' }]);
  const removeStandalone = (i) => setStandaloneSlots(prev => prev.filter((_, j) => j !== i));
  const updateStandalone = (i, name) => setStandaloneSlots(prev => prev.map((s, j) => j === i ? { ...s, name } : s));

  const handleConfirm = () => {
    // Only send new (unlocked) slots — locked ones are already saved
    onConfirm({
      waves: waves.filter(w => w.name.trim()).map(w => ({
        id: w.id ?? null,
        name: w.name.trim(),
        slots: w.slots.filter(s => s.name.trim() && !s.locked).map(s => ({ name: s.name.trim() })),
      })).filter(w => w.slots.length > 0),
      standalone_slots: standaloneSlots.filter(s => s.name.trim() && !s.locked).map(s => ({ name: s.name.trim() })),
      notes: notes.trim() || null,
    });
  };

  const inp = { width: '100%', fontSize: '0.8rem', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', transition: 'border-color 0.15s' };
  const f = (e) => { e.target.style.borderColor = '#dc2626'; };
  const b = (e) => { e.target.style.borderColor = '#e5e7eb'; };

  return (
    <div style={overlay} {...dismiss}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>{addOnly ? 'Ajouter des enquêtes' : 'Passer en Setup'}</div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 1 }}>{project.name}</div>
          </div>
          <button onClick={handleClose} disabled={loading} style={closeBtn}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        {/* Transition line + deadline — only in transition mode */}
        {!addOnly && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <div style={pillInactive}>Avant-Vente</div>
              <div style={{ width: 24, height: 1, background: '#d1d5db' }} />
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626' }} />
              <div style={{ width: 24, height: 1, background: '#dc2626' }} />
              <div style={pillActive}>Setup</div>
            </div>
            {setupDeadline && (
              <div style={{ fontSize: '0.72rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ opacity: 0.5 }} />
                {formatDeadline(setupDeadline)}
              </div>
            )}
          </div>
        )}

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', maxHeight: 'calc(85vh - 200px)' }}>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 16 }}>
                Définissez les enquêtes prévues. Vous pourrez les lier à des enquêtes réelles plus tard.
              </div>

              {/* Waves */}
              {waves.map((wave, wi) => (
                <div key={wi} style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                    <FontAwesomeIcon icon={faLayerGroup} size="sm" style={{ color: '#9ca3af' }} />
                    <input type="text" value={wave.name} onChange={e => updateWaveName(wi, e.target.value)} placeholder="Nom du groupe"
                      disabled={wave.locked} style={{ ...inp, maxWidth: 220, background: wave.locked ? '#f9fafb' : '#fff', color: wave.locked ? '#9ca3af' : '#111827' }} onFocus={f} onBlur={b} />
                    {!wave.locked && (
                      <button onClick={() => removeWave(wi)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: 4 }}><FontAwesomeIcon icon={faTrash} size="sm" /></button>
                    )}
                  </div>
                  <div style={{ padding: '8px 12px' }}>
                    {wave.slots.map((slot, si) => (
                      <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <div style={{ width: 4, height: 4, borderRadius: '50%', background: slot.locked ? '#10b981' : '#d1d5db', flexShrink: 0 }} />
                        <input type="text" value={slot.name} onChange={e => updateWaveSlotName(wi, si, e.target.value)} placeholder="Nom de l'enquête"
                          disabled={slot.locked} style={{ ...inp, background: slot.locked ? '#f9fafb' : '#fff', color: slot.locked ? '#9ca3af' : '#111827' }} onFocus={f} onBlur={b} />
                        {!slot.locked && (
                          <button onClick={() => removeWaveSlot(wi, si)} disabled={wave.slots.length <= 1} style={{ background: 'none', border: 'none', color: wave.slots.length > 1 ? '#d1d5db' : '#f3f4f6', cursor: 'pointer', padding: 2 }}><FontAwesomeIcon icon={faTrash} size="xs" /></button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => addWaveSlot(wi)} style={addBtn}><FontAwesomeIcon icon={faPlus} size="xs" className="me-1" />Enquête</button>
                  </div>
                </div>
              ))}

              <button onClick={addWave} style={{ ...addBtn, marginBottom: 16, display: 'block' }}><FontAwesomeIcon icon={faLayerGroup} size="xs" className="me-1" />Ajouter un groupe</button>

              {/* Standalone */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Enquêtes hors groupe</div>
                {standaloneSlots.map((slot, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: slot.locked ? '#10b981' : '#d1d5db', flexShrink: 0 }} />
                    <input type="text" value={slot.name} onChange={e => updateStandalone(i, e.target.value)} placeholder="Nom de l'enquête"
                      disabled={slot.locked} style={{ ...inp, background: slot.locked ? '#f9fafb' : '#fff', color: slot.locked ? '#9ca3af' : '#111827' }} onFocus={f} onBlur={b} />
                    {!slot.locked && (
                      <button onClick={() => removeStandalone(i)} disabled={standaloneSlots.length <= 1 && waves.length === 0} style={{ background: 'none', border: 'none', color: (standaloneSlots.length > 1 || waves.length > 0) ? '#d1d5db' : '#f3f4f6', cursor: 'pointer', padding: 2 }}><FontAwesomeIcon icon={faTrash} size="xs" /></button>
                    )}
                  </div>
                ))}
                <button onClick={addStandalone} style={addBtn}><FontAwesomeIcon icon={faPlus} size="xs" className="me-1" />Enquête</button>
              </div>

              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>Commentaire</div>
                <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optionnel..." style={{ ...inp, resize: 'vertical' }} onFocus={f} onBlur={b} />
              </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={handleClose} disabled={loading} style={cancelBtnStyle}>Annuler</button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              ...confirmBtnStyle,
              background: (loading) ? '#e5e7eb' : '#dc2626',
              color: (loading) ? '#9ca3af' : '#fff',
              cursor: (loading) ? 'default' : 'pointer',
            }}
          >
            {loading ? 'En cours...' : addOnly ? 'Ajouter' : 'Passer en Setup'}
          </button>
        </div>
      </div>
      <style>{`@keyframes terrainModalIn { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
    </div>
  );
};

const overlay = { position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' };
const modal = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden', animation: 'terrainModalIn 0.2s ease' };
const header = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 };
const closeBtn = { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 };
const footerStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '1px solid #f3f4f6', flexShrink: 0 };
const cancelBtnStyle = { padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' };
const confirmBtnStyle = { padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: '0.78rem', fontWeight: 600 };
const pillInactive = { fontSize: '0.72rem', fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: '#f3f4f6', color: '#6b7280', whiteSpace: 'nowrap' };
const pillActive = { fontSize: '0.72rem', fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: '#111827', color: '#fff', whiteSpace: 'nowrap' };
const addBtn = { fontSize: '0.7rem', padding: '3px 8px', borderRadius: 4, border: '1px dashed #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer', marginTop: 2 };

export default TerrainTransitionModal;
